import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './sidebar';
import { LoadingBar } from '../ui/loading-bar';
import { useMobileViewport } from '../../lib/breakpoints';
import { cn } from '../../lib/utils';

export function Layout() {
  const isMobileViewport = useMobileViewport();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarToggleRef = useRef<HTMLButtonElement | null>(null);
  const sidebarDrawerRef = useRef<HTMLDivElement | null>(null);

  const closeMobileSidebar = () => {
    setSidebarOpen(false);
    sidebarToggleRef.current?.focus();
  };

  useEffect(() => {
    if (isMobileViewport) return;
    setSidebarOpen(false);
  }, [isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport || !sidebarOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSidebarOpen(false);
        sidebarToggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileViewport, sidebarOpen]);

  useEffect(() => {
    if (!isMobileViewport || !sidebarOpen) return;
    sidebarDrawerRef.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, [isMobileViewport, sidebarOpen]);

  return (
    <div data-testid="app-shell" className="flex h-dvh overflow-hidden">
      <LoadingBar />
      {isMobileViewport ? (
        <>
          <div
            aria-hidden="true"
            data-testid="sidebar-backdrop"
            onClick={closeMobileSidebar}
            className={cn(
              'fixed inset-0 z-40 bg-ink/50 transition-opacity duration-200',
              sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
          />
          {/* 105-02 (#155): a clipping host owns the fixed positioning so the
              translated panel never extends a border box past the viewport's
              left edge — WebKit counts off-screen fixed boxes into the
              document's scrollable overflow (Blink does not), which kept the
              page horizontally scrollable on iOS Safari. The host is
              shrink-to-fit around its in-flow child, so its own box stays at
              x∈[0, sidebar width] and its clip keeps the slide-out panel from
              reaching the document. No transform/filter/backdrop-filter may
              ever land on this chain — they would re-anchor the fixed host to
              page coordinates (layout.test.tsx pins this).
              105-05 (#158): shrink-to-fit keeps the host a 248px full-height
              box even while the drawer is closed — a transparent strip eating
              every tap over the left two thirds of a phone. The closed state
              must drop it from hit-testing and painting, like the backdrop
              above; it stays mounted so both transitions keep running. */}
          <div
            className={cn(
              'fixed inset-y-0 left-0 z-50 overflow-hidden',
              !sidebarOpen && 'pointer-events-none invisible'
            )}
          >
            <div
              ref={sidebarDrawerRef}
              className={cn(
                'flex h-full transition-[transform,visibility] duration-200 ease-out',
                sidebarOpen ? 'translate-x-0' : 'invisible -translate-x-full'
              )}
            >
              <Sidebar onNavigate={closeMobileSidebar} />
            </div>
          </div>
        </>
      ) : (
        <Sidebar />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* top bar */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-surface/80 px-7 backdrop-blur">
          <div className="flex items-center gap-3">
            {isMobileViewport && (
              <button
                type="button"
                ref={sidebarToggleRef}
                aria-controls="admin-sidebar"
                aria-expanded={sidebarOpen}
                aria-label="Toggle navigation menu"
                onClick={() => setSidebarOpen((open) => !open)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-line/50 hover:text-ink"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                >
                  <path d="M2.5 4.5h11" />
                  <path d="M2.5 8h11" />
                  <path d="M2.5 11.5h11" />
                </svg>
              </button>
            )}
            <nav className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-ink">Lead Ops Console</span>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-muted">Stage A Admin</span>
          </div>
        </header>

        {/* work surface */}
        <main id="work" className="grain flex-1 overflow-y-auto px-7 py-7">
          <div className="mx-auto">
            <Outlet />
          </div>
        </main>

        {/* build stamp (epic 028) — px-7 matches the header's horizontal padding */}
        <footer className="px-7 py-2 text-xs text-muted">
          build {import.meta.env.VITE_BUILD_SHA || 'unknown'}
        </footer>
      </div>
    </div>
  );
}
